import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, MailQuestion } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    name: "Part-Time Flex",
    hours: "20 hrs/week",
    target: "Solo practices & part-time clinics",
    features: [
      "Dedicated HIPAA-trained VMA",
      "Patient calling & scheduling",
      "Prior auth — medicines & radiology",
      "US account manager",
      "Daily shift logs",
    ],
    featured: false,
  },
  {
    name: "Full-Time Dedicated",
    hours: "40 hrs/week",
    target: "Growing multi-provider practices",
    features: [
      "Dedicated full-time VMA",
      "Prior auth — medicines, radiology & infusion",
      "Referral management & lab ordering",
      "Fax uploading & voicemail triage",
      "Backup VMA coverage included",
      "Weekly performance audits",
    ],
    featured: true,
  },
  {
    name: "Practice Fleet",
    hours: "Multiple VMAs",
    target: "Large health systems & specialty groups",
    features: [
      "Custom VMA team deployment",
      "Custom EHR SOP training",
      "Dedicated operations director",
      "SLA guarantee & auditing",
      "Custom EHR integrations",
    ],
    featured: false,
  },
];

export const Pricing = () => {
  const [inquiry, setInquiry] = useState({ name: "", email: "", plan: "" });
  const [sending, setSending] = useState(false);

  const requestPricing = (plan) => {
    setInquiry((f) => ({ ...f, plan }));
    scrollToSection("#price-inquiry");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!inquiry.name.trim() || !inquiry.email.trim() || !inquiry.plan) {
      toast.error("Please add your name, email, and the plan you're curious about.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: inquiry.name,
        email: inquiry.email,
        phone: "",
        practice_name: "",
        specialty: "",
        providers_needed: "",
        message: `Price inquiry — interested in: ${inquiry.plan}`,
      });
      toast.success("Price inquiry sent — exact rates will land in your inbox within one business day.");
      setInquiry({ name: "", email: "", plan: "" });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many submissions. Please try again in a few minutes.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="pricing" className="py-20 lg:py-28 bg-[#0A261E]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <div className="flex justify-center">
            <Eyebrow dark>Simple, Affordable Plans</Eyebrow>
          </div>
          <h2 className="mt-4 mx-auto max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-[#F5F7F6]">
            A full back office for less than one salary.
          </h2>
          <p className="mt-4 mx-auto max-w-xl text-sm sm:text-base leading-relaxed text-[#A3B8B0]">
            Every practice is different, so every quote is tailored. Tell us which plan fits and we'll send exact,
            no-obligation pricing to your inbox.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3" data-testid="pricing-plans">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div
                data-testid={`pricing-card-${i}`}
                className={`relative flex h-full flex-col rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1.5 ${
                  p.featured
                    ? "border-[#EAB308]/40 bg-[#FAF9F5] shadow-2xl"
                    : "border-white/10 bg-[#123328]"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#EAB308] px-4 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0A261E]">
                    Most Popular
                  </span>
                )}
                <p className={`font-mono text-xs uppercase tracking-[0.2em] ${p.featured ? "text-[#4A5D55]" : "text-[#A3B8B0]"}`}>
                  {p.target}
                </p>
                <h3 className={`mt-2 font-display text-2xl font-bold ${p.featured ? "text-[#0F1E19]" : "text-[#F5F7F6]"}`}>
                  {p.name}
                </h3>
                <div className="mt-6">
                  <span
                    className={`font-mono text-3xl font-semibold ${p.featured ? "text-[#0A3B2C]" : "text-[#F5F7F6]"}`}
                    data-testid={`pricing-hours-${i}`}
                  >
                    {p.hours}
                  </span>
                  <p className={`mt-1.5 text-sm ${p.featured ? "text-[#4A5D55]" : "text-[#A3B8B0]"}`}>
                    Tailored monthly rate — quoted for your practice
                  </p>
                </div>
                <ul className="mt-7 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-[#059669]" : "text-[#EAB308]"}`} />
                      <span className={`text-sm ${p.featured ? "text-[#0F1E19]" : "text-[#F5F7F6]/90"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${i}`}
                  onClick={() => requestPricing(p.name)}
                  className={`group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-all ${
                    p.featured
                      ? "bg-[#0A3B2C] text-[#FAF9F5] hover:bg-[#072B20]"
                      : "border border-white/20 text-[#F5F7F6] hover:bg-white/10"
                  }`}
                >
                  Request Pricing
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div
            id="price-inquiry"
            className="mt-14 rounded-3xl border border-[#EAB308]/30 bg-[#123328] p-8 sm:p-10"
            data-testid="price-inquiry-box"
          >
            <div className="grid items-center gap-8 lg:grid-cols-2">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#EAB308]/30 bg-[#EAB308]/10 px-3 py-1.5">
                  <MailQuestion className="h-3.5 w-3.5 text-[#EAB308]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#EAB308]">Price Inquiry</span>
                </div>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl font-bold text-[#F5F7F6]">
                  Want the exact numbers?
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-[#A3B8B0]">
                  Drop your name, email, and the plan you're eyeing — a US-based advisor sends a tailored quote within
                  one business day. No obligation, no pushy sales calls.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="price-inquiry-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    data-testid="price-inquiry-name-input"
                    placeholder="Your name"
                    className="border-white/15 bg-[#0A261E] text-[#F5F7F6] placeholder:text-[#A3B8B0]/50"
                    value={inquiry.name}
                    onChange={(e) => setInquiry((f) => ({ ...f, name: e.target.value }))}
                    required
                  />
                  <Input
                    type="email"
                    data-testid="price-inquiry-email-input"
                    placeholder="Work email"
                    className="border-white/15 bg-[#0A261E] text-[#F5F7F6] placeholder:text-[#A3B8B0]/50"
                    value={inquiry.email}
                    onChange={(e) => setInquiry((f) => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>
                <Select value={inquiry.plan} onValueChange={(v) => setInquiry((f) => ({ ...f, plan: v }))}>
                  <SelectTrigger data-testid="price-inquiry-plan-select" className="border-white/15 bg-[#0A261E] text-[#F5F7F6]">
                    <SelectValue placeholder="Which plan are you curious about?" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Part-Time Flex", "Full-Time Dedicated", "Practice Fleet", "Not sure yet — advise me"].map((p) => (
                      <SelectItem key={p} value={p} data-testid={`plan-option-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="price-inquiry-submit-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#EAB308] px-6 py-3.5 text-sm font-bold text-[#0A261E] transition-all hover:brightness-110 disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send My Price Inquiry
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-10 text-center font-mono text-xs text-[#A3B8B0]">
            No long-term contracts · Cancel anytime with 30 days notice · Replacement guarantee included
          </p>
        </Reveal>
      </div>
    </section>
  );
};
